import useTranslation from 'next-translate/useTranslation'
import { FC } from 'react'
import EmailIcon from '@rsuite/icons/Email'
import PhoneIcon from '@rsuite/icons/Phone'
import { FlexboxGrid } from 'rsuite'
import { useRouter } from 'next/router'
import { Crisp } from 'crisp-sdk-web'

import ChatIcon from '../../Icons/LandingPage/ChatIcon'
import { getCountryEmail, getCountryPhone } from '../../../utils/countries'
import { ActiveCountryId } from '../../../utils/constants'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import contactEvent from '../../../utils/analytics/contactEvent'
import useTrackingContext from '../../../services/trackingContext/useTrackingContext'
import { AnalyticsContext, ContactContext } from '../../../utils/types/analytics'

import styles from '../../../styles/FooterContactUsSection.module.less'

const FooterContactUsSection: FC = () => {
  const { t } = useTranslation('common')
  const { locale: countryAndLocale, pathname } = useRouter()
  const { country } = getCountryAndLocaleStrings(countryAndLocale)

  // Get global tracking object for analytics events
  const { state: globalTrackingObject } = useTrackingContext()

  // analyticsContactContext is deprecated,
  // it will be removed after a set period of time.
  const analyticsContactContext: ContactContext = globalTrackingObject.contact_event?.contact_context || 'contact_modal'
  const analyticsContext: AnalyticsContext = globalTrackingObject.contact_event?.context || 'direct'

  // Don't show component on checkout page
  if (pathname?.startsWith('/checkout')) {
    return null
  }

  return (
    <div className={styles['footer-contact-us-section']}>
      <h2>
        {t('modals:Need help?')}
      </h2>
      <p className={styles['contact-us-text']}>
        {t('Get in touch with our customer support if you need help')}
      </p>

      <FlexboxGrid
        justify="center"
        className={styles['options-items']}
      >
        <FlexboxGrid
          align="middle"
          className={`${styles['contact-option']} cursor-pointer`}
          onClick={() => Crisp.chat.open()}
        >
          <FlexboxGrid.Item className={styles['contact-icon']}>
            <ChatIcon />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item className="flex-shrink-col-dynamic">
            <h4 className={styles.title}>
              {(t('modals:Chat with us'))}
            </h4>
            <p className={styles.text}>{t('modals:Get help from our experts')}</p>
          </FlexboxGrid.Item>
        </FlexboxGrid>

        <FlexboxGrid
          align="middle"
          as="a"
          href={`mailto:${getCountryEmail(country as ActiveCountryId)}`}
            // ANALYTICS-EVENT: Contact event
          className={styles['contact-option']}
          onClick={() => contactEvent(analyticsContactContext, analyticsContext)}
        >
          <FlexboxGrid.Item className={styles['contact-icon']}>
            <EmailIcon
              color="#fff"
              height={20}
              width={20}
            />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item className="flex-shrink-col-dynamic">
            <h4 className={styles.title}>
              {(t('Send us an email'))}
            </h4>

            <b className={styles['primary-darker']}>
              {getCountryEmail(country as ActiveCountryId)}
            </b>
          </FlexboxGrid.Item>
        </FlexboxGrid>

        <FlexboxGrid
          align="middle"
          as="a"
          href={`tel:${getCountryPhone(country as ActiveCountryId)}`}
          onClick={() => contactEvent(analyticsContactContext, analyticsContext)}
          className={styles['contact-option']}
        >
          <FlexboxGrid.Item className={styles['contact-icon']}>
            <PhoneIcon
              color="#fff"
              height={20}
              width={20}
            />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item className="flex-shrink-col-dynamic">
            <h4 className={styles.title}>
              {(t('modals:Give us a call'))}
            </h4>
            <FlexboxGrid
              align="middle"
              className="margin-top-spacer-half"
            >
              <b className={styles['primary-darker']}>{getCountryPhone(country as ActiveCountryId)}</b>
              <p className={styles['contact-time']}>
                (9:00 - 18:00)
              </p>
            </FlexboxGrid>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </FlexboxGrid>
    </div>
  )
}

export default FooterContactUsSection
